import React, { useEffect, useState } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Panel,
  theme,
  Spinner,
} from '@xometry/xometry_loft';
import { css } from '@emotion/react';

import {
  AcceptOrganizationInviteQuery,
  GetOrganizationByIdQuery,
} from '../../../schema/generated/schema';
import { WWW_ORIGIN } from '../../../config';

import Helpers, { ORGANIZATION_INVITE_NOT_PENDING } from './helpers';
import quotesApi from '../../../services/quotes';
import IncompleteLinkPage from './IncompleteLinkPage';

const AcceptInvite = () => {
  const [orgName, setOrgName] = useState('');
  const [queryStringParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const orgId = queryStringParams.get('orgId');
  const acceptInvite = queryStringParams.get('acceptInvite');
  const redirectUrl = queryStringParams.get('redirect');

  const validatedRedirectUrl = Helpers.getValidatedRedirectUrl(redirectUrl);

  const [getOrganizationData, { data: organizationData, error: getOrgError }] =
    useLazyQuery(GetOrganizationByIdQuery);

  const [
    acceptOrganizatonInviteAction,
    { data: acceptInviteData, error: acceptInviteError },
  ] = useMutation(AcceptOrganizationInviteQuery);

  useEffect(() => {
    if (orgId) {
      acceptOrganizatonInviteAction({
        variables: {
          orgId,
          acceptInvite: acceptInvite === 'true' ? true : false,
        },
      });
    }
  }, []);

  useEffect(() => {
    // Get a new token with the newly joined org
    quotesApi.refreshAuthToken();
    if (
      acceptInviteData ||
      acceptInviteError?.message === ORGANIZATION_INVITE_NOT_PENDING
    ) {
      getOrganizationData({
        variables: {
          id: orgId,
        },
      });
    }
  }, [acceptInviteData, acceptInviteError?.message]);

  useEffect(() => {
    if (organizationData) {
      setOrgName(organizationData.getOrganizationById.name);
      setIsLoading(false);
    }
  }, [organizationData]);

  const pageCopy = Helpers.getPageCopy(
    orgName,
    acceptInvite,
    getOrgError?.message,
    acceptInviteError?.message,
    isLoading
  );

  const isError = !!(
    getOrgError?.message ||
    acceptInviteError?.message ||
    !orgId
  );

  const handleRedirect = () => {
    isError && acceptInviteError?.message != ORGANIZATION_INVITE_NOT_PENDING
      ? (window.location.href = `${WWW_ORIGIN}/quoting/home`)
      : (window.location.href = validatedRedirectUrl);
  };

  if (isLoading && !orgName && !isError) {
    return <Spinner />;
  }

  if (!orgName && !isError && !isLoading) {
    return (
      <Box width="100%">
        <Panel
          padding={theme.space[4]}
          width={['460px', '680px']}
          margin="auto"
          paddingTop={theme.space[6]}
          marginTop={theme.space[6]}
        >
          <IncompleteLinkPage />
        </Panel>
      </Box>
    );
  }

  return (
    <Box width="100%">
      <Panel
        padding={theme.space[0]}
        width={['330px', '680px']}
        marginX="auto"
        marginY="80px"
        paddingTop={theme.space[6]}
      >
        <Flex flexDirection="column" alignItems="center">
          <Box
            fontSize="28px"
            fontWeight={theme.fontWeights.regular}
            lineHeight="36px"
            marginX={[theme.space[5], '65px']}
            marginBottom={theme.space[4]}
            color={theme.colors.darkBlues[100]}
            textAlign="center"
          >
            {pageCopy.title}
          </Box>
          <Box
            fontSize={theme.fontSizes[2]}
            fontWeight={theme.fontWeights.regular}
            lineHeight={theme.lineHeights[3]}
            marginX={[theme.space[8], '83px']}
            marginBottom={theme.space[6]}
            color={theme.colors.darkBlues[100]}
          >
            {pageCopy.body()}
          </Box>

          <img
            src={`https://assets.xometry.com/spot-team-discussion.svg`}
            css={css`
              width: 63%;
            `}
            alt="Accept Organization Invitation"
          />
          <Button
            marginTop={theme.space[5]}
            marginBottom={theme.space[9]}
            paddingY={theme.space[2]}
            width={['80%', '66%']}
            kind={theme.kind.primary}
            onClick={handleRedirect}
          >
            Continue{' '}
            <Box display={['none', 'inline']}>
              {isError &&
              acceptInviteError?.message != ORGANIZATION_INVITE_NOT_PENDING
                ? 'to Dashboard'
                : 'to Teamspace'}
            </Box>
          </Button>
        </Flex>
      </Panel>
    </Box>
  );
};

export default AcceptInvite;
